import React from 'react';
import {
  clearAccountFavoritesAPI,
  clearAccountHistoryAPI,
  deleteAccountFavoriteAPI,
  deleteAccountHistoryAPI,
  getAccountDataAPI,
  importAccountLocalDataAPI,
  saveAccountFavoriteAPI,
  saveAccountHistoryAPI,
  updateAccountHistoryTitleAPI,
} from '../utils/api';
import {
  addHistoryEntryToItems,
  createFavoriteEntry,
  createHistoryEntry,
  favoriteSignatureFor,
  normalizeFavorites,
  normalizeHistoryItems,
  updateHistoryTitleInItems,
} from '../utils/savedData';

function importPromptKey(userId) {
  return `vegai.accountImportPrompt.v1.${userId}`;
}

export function useAccountData({
  user,
  session,
  localFavorites = [],
  localHistory = [],
} = {}) {
  const token = session?.access_token || '';
  const userId = user?.id || '';
  const signedIn = Boolean(userId && token);
  const [favorites, setFavorites] = React.useState([]);
  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loadedUserId, setLoadedUserId] = React.useState('');
  const [error, setError] = React.useState('');
  const [importOffer, setImportOffer] = React.useState(null);
  const [importing, setImporting] = React.useState(false);

  const loadData = React.useCallback(async () => {
    if (!signedIn) return null;
    setLoading(true);
    try {
      const data = await getAccountDataAPI(token);
      setFavorites(normalizeFavorites(data.favorites));
      setHistory(normalizeHistoryItems(data.history));
      setLoadedUserId(userId);
      setError('');
      return data;
    } catch (err) {
      setError(err?.message || 'Could not load your synced data.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [signedIn, token, userId]);

  React.useEffect(() => {
    if (!signedIn) {
      setFavorites([]);
      setHistory([]);
      setLoadedUserId('');
      setImportOffer(null);
      setError('');
      return;
    }
    setFavorites([]);
    setHistory([]);
    setLoadedUserId('');
    loadData().catch(() => {});
  }, [signedIn, userId, loadData]);

  React.useEffect(() => {
    if (!signedIn || loadedUserId !== userId || importOffer) return;
    if (localFavorites.length === 0 && localHistory.length === 0) return;
    try {
      if (localStorage.getItem(importPromptKey(userId)) === 'done') return;
    } catch {
      return;
    }
    setImportOffer({
      favorites: localFavorites.length,
      history: localHistory.length,
    });
  }, [
    signedIn,
    loadedUserId,
    userId,
    importOffer,
    localFavorites.length,
    localHistory.length,
  ]);

  const markImportPromptDone = React.useCallback(() => {
    try {
      localStorage.setItem(importPromptKey(userId), 'done');
    } catch {
      // The import choice still applies for this page even if storage is blocked.
    }
    setImportOffer(null);
  }, [userId]);

  const importLocalData = React.useCallback(async () => {
    if (!signedIn) return;
    setImporting(true);
    try {
      const data = await importAccountLocalDataAPI(token, {
        favorites: localFavorites,
        history: localHistory,
      });
      setFavorites(normalizeFavorites(data.favorites));
      setHistory(normalizeHistoryItems(data.history));
      setError('');
      markImportPromptDone();
    } catch (err) {
      setError(err?.message || 'Could not import this device data.');
      throw err;
    } finally {
      setImporting(false);
    }
  }, [signedIn, token, localFavorites, localHistory, markImportPromptDone]);

  const refreshAfter = React.useCallback((promise) => {
    promise
      .then(() => loadData())
      .catch((err) => {
        setError(err?.message || 'Could not sync that change.');
      });
  }, [loadData]);

  const addFavorite = React.useCallback((entry) => {
    const cleaned = createFavoriteEntry(entry);
    if (!cleaned) return null;
    setFavorites((current) => {
      const signature = favoriteSignatureFor(cleaned);
      return normalizeFavorites([
        cleaned,
        ...current.filter((item) => favoriteSignatureFor(item) !== signature),
      ]);
    });
    refreshAfter(saveAccountFavoriteAPI(token, cleaned));
    return cleaned;
  }, [token, refreshAfter]);

  const removeFavorite = React.useCallback((id) => {
    setFavorites((current) => current.filter((item) => item.id !== id));
    refreshAfter(deleteAccountFavoriteAPI(token, id));
  }, [token, refreshAfter]);

  const removeFavoriteEntry = React.useCallback((entry) => {
    const signature = favoriteSignatureFor(entry);
    const existing = favorites.find((item) => favoriteSignatureFor(item) === signature);
    if (existing) removeFavorite(existing.id);
  }, [favorites, removeFavorite]);

  const toggleFavorite = React.useCallback((entry) => {
    if (!entry) return null;
    const signature = favoriteSignatureFor(entry);
    const existing = favorites.find((item) => favoriteSignatureFor(item) === signature);
    if (existing) {
      removeFavorite(existing.id);
      return null;
    }
    return addFavorite(entry);
  }, [favorites, addFavorite, removeFavorite]);

  const clearFavorites = React.useCallback(() => {
    setFavorites([]);
    refreshAfter(clearAccountFavoritesAPI(token));
  }, [token, refreshAfter]);

  const isFavoriteSaved = React.useCallback((entry) => {
    if (!entry) return false;
    const signature = favoriteSignatureFor(entry);
    return favorites.some((item) => favoriteSignatureFor(item) === signature);
  }, [favorites]);

  const addHistory = React.useCallback((entry) => {
    const item = createHistoryEntry(entry);
    setHistory((current) => addHistoryEntryToItems(current, item, item.ts));
    refreshAfter(saveAccountHistoryAPI(token, item));
  }, [token, refreshAfter]);

  const removeHistory = React.useCallback((id) => {
    setHistory((current) => current.filter((item) => item.id !== id));
    refreshAfter(deleteAccountHistoryAPI(token, id));
  }, [token, refreshAfter]);

  const updateHistoryTitle = React.useCallback((id, title) => {
    setHistory((current) => updateHistoryTitleInItems(current, id, title));
    refreshAfter(updateAccountHistoryTitleAPI(token, id, title));
  }, [token, refreshAfter]);

  const clearHistory = React.useCallback(() => {
    setHistory([]);
    refreshAfter(clearAccountHistoryAPI(token));
  }, [token, refreshAfter]);

  const favoritesApi = React.useMemo(() => ({
    items: favorites,
    add: addFavorite,
    remove: removeFavorite,
    removeEntry: removeFavoriteEntry,
    toggle: toggleFavorite,
    clear: clearFavorites,
    isSaved: isFavoriteSaved,
  }), [
    favorites,
    addFavorite,
    removeFavorite,
    removeFavoriteEntry,
    toggleFavorite,
    clearFavorites,
    isFavoriteSaved,
  ]);

  const historyApi = React.useMemo(() => ({
    items: history,
    add: addHistory,
    remove: removeHistory,
    updateTitle: updateHistoryTitle,
    clear: clearHistory,
    reload: loadData,
  }), [
    history,
    addHistory,
    removeHistory,
    updateHistoryTitle,
    clearHistory,
    loadData,
  ]);

  return {
    signedIn,
    loading,
    error,
    importOffer,
    importing,
    importLocalData,
    dismissImport: markImportPromptDone,
    favorites: favoritesApi,
    history: historyApi,
  };
}
